<template>
  <div class="my-account-security">
    <div v-if="!mfa" class="mas-loading">
      <span>{{ loadError || "불러오는 중입니다." }}</span>
      <a-button v-if="loadError" @click="load">다시 시도</a-button>
    </div>
    <template v-else>
      <div class="mas-row">
        <span class="mas-label">2차 인증</span>
        <div class="mas-body">
          <div class="mas-status">
            <!-- 수단이 있으면 「2차 인증 사용」 스위치(필수 계정은 잠금), 없으면 켜짐/꺼짐 배지 + 등록 안내(D-132, U1) -->
            <template v-if="hasFactor">
              <a-switch
                :checked="mfa.required"
                :disabled="busy || enforced"
                aria-labelledby="mas-switch-label"
                @change="toggle"
              />
              <span id="mas-switch-label" class="mas-switch-label">2차 인증 사용</span>
            </template>
            <span
              v-else
              class="tf-badge"
              :class="mfa.required ? 'tf-badge--on' : 'tf-badge--off'"
            >
              {{ mfa.required ? "켜짐" : "꺼짐" }}
            </span>
            <span v-if="requiredTag" class="tf-badge tf-badge--required">
              {{ requiredTag }}
            </span>
          </div>
          <p class="mas-hint">{{ statusText }}</p>
          <p
            v-if="toggleResult"
            :class="toggleResult.error ? 'mas-result-error' : 'mas-result'"
            :role="toggleResult.error ? 'alert' : 'status'"
          >
            {{ toggleResult.text }}
          </p>
        </div>
      </div>
      <div v-for="row in rows" :key="row.method" class="mas-row">
        <span class="mas-label">{{ row.label }}</span>
        <div class="mas-body mas-factor">
          <span class="mas-value" :class="{ 'is-empty': !row.masked }">
            {{ row.masked || "등록 안 됨" }}
          </span>
          <!-- 버튼 모양은 같은 페이지 비밀번호 「변경하기」(노란 주 버튼)·「취소하기」(회색)와 맞춤(#12). 지우기 경고는 확인창에서 -->
          <a-button type="primary" :disabled="busy" @click="enroll(row.method)">
            {{ row.masked ? "변경" : "등록" }}
          </a-button>
          <a-button
            v-if="row.masked"
            :disabled="busy || row.locked"
            @click="remove(row.method)"
          >
            삭제
          </a-button>
        </div>
      </div>
      <p v-if="hasLocked" class="mas-hint mas-note">{{ enforcedMessage }}</p>
      <p v-if="error" class="mas-error" role="alert">{{ error }}</p>
    </template>
    <otp-verify-modal ref="otp" />
  </div>
</template>

<script>
import OtpVerifyModal from "@/views/components/OtpVerifyModal.vue";
import { twoBtnDialog } from "@/utils/utils";
import {
  ENFORCED_MESSAGE,
  alreadyNotified,
  authErrorMessage,
  storeRenewedTokens,
} from "@/utils/mfa";

// via: 남은 수단으로 받는다는 문장용(받침에 맞춘 조사)
const FACTORS = [
  { method: "ALIMTALK_OTP", key: "phoneMasked", label: "휴대폰", via: "휴대폰으로" },
  { method: "FALLBACK_EMAIL_OTP", key: "fallbackEmailMasked", label: "이메일", via: "이메일로" },
];

// 「내 계정 보안」: 본인 휴대폰·이메일 수단 등록·변경·삭제(SPEC-SENDCERT-001 design.md §5-7 ③, §6-1 A14~A16)와
// 2차 인증 끄기·켜기(D-132, A17 PUT /auth/2fa/enabled). 계정 메뉴(이름을 누르면 뜨는 메뉴)의 창에 들어감(U2).
// 등록·변경은 인증번호 창의 세션 중 등록 모드, 삭제는 확인창 뒤 DELETE /auth/2fa/factors/:method. 끝나면 프로필을 다시 읽음.
// 줄은 페이지 격자(이름표 120px)에 맞춤. 켜짐/꺼짐·필수 배지(.tf-badge)는 앱 ant-design.scss.
// admin-hospital·admin-manufacturer 가 같은 파일을 씀.
export default {
  name: "MyAccountSecurity",
  components: {
    OtpVerifyModal,
  },
  data() {
    return {
      mfa: null,
      loadError: "",
      error: "",
      busy: false,
      // 스위치 아래 결과 한 줄 { text, error }
      toggleResult: null,
      enforcedMessage: ENFORCED_MESSAGE,
    };
  },
  computed: {
    // 필수 계정(운영팀·회사 강제)은 마지막 수단을 지울 수 없음(서버도 TWO_FACTOR_ENFORCED)
    enforced() {
      return (
        this.mfa?.companyRequired === true ||
        ["OPERATOR", "COMPANY"].includes(this.mfa?.requiredBy)
      );
    },
    // 본인이 켠 2차 인증(SELF)은 '필수'로 표시하지 않음(D6). 이유는 아래 안내 줄에
    requiredTag() {
      return this.enforced ? "필수" : "";
    },
    hasFactor() {
      return this.rows.some((row) => row.masked);
    },
    // 언제 쓰이는지·등록하면 켜지는지(#10), 본인이 끈 상태(selfDisabled, D-132). 운영자인지는 role 이 아니라 서버 requiredBy 로
    statusText() {
      if (this.enforced) {
        const reason =
          this.mfa.requiredBy === "OPERATOR"
            ? "운영자 계정은 2차 인증이 필수입니다."
            : "회사 정책으로 2차 인증이 필수입니다.";
        return this.hasFactor
          ? `${reason} 로그인할 때 등록된 휴대폰이나 이메일로 받은 인증번호를 입력합니다.`
          : `${reason} 휴대폰이나 이메일을 등록해 두세요. 등록하지 않으면 다음 로그인 때 등록해야 합니다.`;
      }
      if (!this.hasFactor)
        return "휴대폰이나 이메일을 등록하면 켜지고, 로그인할 때 인증번호를 입력합니다.";
      return this.mfa.required
        ? "휴대폰이나 이메일이 등록돼 있어 로그인할 때 인증번호를 입력합니다."
        : "꺼져 있어 로그인할 때 인증번호를 묻지 않습니다. 등록한 휴대폰·이메일은 남아 있어 언제든 다시 켤 수 있습니다.";
    },
    rows() {
      const mfa = this.mfa || {};
      const count = FACTORS.filter((f) => mfa[f.key]).length;
      return FACTORS.map((f) => ({
        ...f,
        masked: mfa[f.key],
        locked: this.enforced && count === 1 && !!mfa[f.key],
      }));
    },
    hasLocked() {
      return this.rows.some((row) => row.locked);
    },
  },
  mounted() {
    return this.load();
  },
  methods: {
    async load() {
      this.loadError = "";
      try {
        const { data } = await this.$apiInstance.get("/auth/profile");
        this.mfa = data?.mfa || null;
        this.loadError = this.mfa ? "" : "2차 인증 정보를 불러오지 못했습니다.";
      } catch (error) {
        console.log(error);
        this.loadError = "2차 인증 정보를 불러오지 못했습니다.";
      }
    },
    // 새 증표·토큰 저장은 인증번호 창이 함. 등록돼 있으면 「변경」(지금 값을 창에 보임)
    async enroll(method) {
      this.error = "";
      this.toggleResult = null;
      const row = this.rows.find((r) => r.method === method);
      const done = await this.$refs.otp.start({
        mode: "enroll",
        availableMethods: [method],
        ...(row?.masked && { currentMasked: row.masked }),
      });
      if (done) await this.load();
    },
    // 지우기 전에 대상과 결과를 말하는 확인창(D7). 삭제 응답에도 새 토큰이 옴(mfaReq 다시 계산, 감사 D6)
    async remove(method) {
      const row = this.rows.find((r) => r.method === method);
      const other = this.rows.find((r) => r.masked && r.method !== method);
      const confirmed = await twoBtnDialog(
        `${row.label} 삭제`,
        `등록된 ${row.label}(${row.masked})을 지웁니다. ${
          other
            ? `앞으로 ${other.via} 인증번호를 받습니다.`
            : "지우면 2차 인증이 꺼집니다."
        }`,
        "삭제",
        "취소",
        { confirmStyle: "danger", focusCancel: true }
      );
      if (!confirmed) return;
      this.busy = true;
      this.error = "";
      this.toggleResult = null;
      try {
        const { data } = await this.$apiInstance.delete(
          `/auth/2fa/factors/${method}`
        );
        storeRenewedTokens(data);
      } catch (error) {
        console.log(error);
        if (!alreadyNotified(error)) {
          this.error =
            authErrorMessage(error) ||
            error.response?.data?.message ||
            "수단을 지우지 못했습니다.";
        }
      } finally {
        this.busy = false;
      }
      await this.load();
    },
    // 「2차 인증 사용」 스위치(A17). 끄기는 확인창 뒤, 켜기는 바로. 끌 때 증표가 없으면(403 MFA_REQUIRED)
    // axios 인터셉터가 본인 확인 창을 띄우고 같은 요청을 다시 보냄. 응답의 새 토큰은 삭제와 같게 저장.
    // 스위치는 :checked 로만 움직여서 서버가 받아들인 뒤 다시 읽은 값으로 바뀜(실패·취소면 그대로)
    async toggle(enabled) {
      if (this.busy || enabled === this.mfa?.required) return;
      if (!enabled) {
        const confirmed = await twoBtnDialog(
          "2차 인증 끄기",
          "2차 인증을 끕니다. 로그인하거나 메시지를 보낼 때 인증번호를 묻지 않습니다. 등록한 휴대폰·이메일은 그대로 남아 언제든 다시 켤 수 있습니다.",
          "끄기",
          "취소",
          { confirmStyle: "danger", focusCancel: true }
        );
        if (!confirmed) return;
      }
      this.busy = true;
      this.error = "";
      this.toggleResult = null;
      try {
        const { data } = await this.$apiInstance.put("/auth/2fa/enabled", {
          enabled,
        });
        storeRenewedTokens(data);
        this.toggleResult = {
          text: enabled ? "2차 인증을 켰습니다." : "2차 인증을 껐습니다.",
          error: false,
        };
      } catch (error) {
        console.log(error);
        // 필수 계정(403 TWO_FACTOR_REQUIRED_BY_POLICY)·수단 없음(400)·대리 세션 등은 서버 문구. 다시 읽으면 필수 계정은 잠김
        if (!alreadyNotified(error)) {
          this.toggleResult = {
            text:
              authErrorMessage(error) ||
              error.response?.data?.message ||
              `2차 인증을 ${enabled ? "켜지" : "끄지"} 못했습니다.`,
            error: true,
          };
        }
      } finally {
        this.busy = false;
      }
      await this.load();
    },
  },
};
</script>

<style lang="scss">
.my-account-security {
  p {
    margin: 0;
    word-break: keep-all;
  }
  .mas-loading {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 40px;
    color: #4b5158;
    font-size: 14px;
  }
  .mas-row {
    display: flex;
    align-items: flex-start;
    & + .mas-row {
      margin-top: 10px;
    }
  }
  .mas-label {
    flex: 0 0 120px;
    line-height: 40px;
    color: #2f3337;
    font-size: 15px;
  }
  .mas-body {
    flex: 1;
    min-width: 0;
  }
  .mas-status {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 40px;
  }
  .mas-switch-label {
    margin: 0 4px 0 2px;
    color: #17191c;
    font-size: 15px;
  }
  .mas-result,
  .mas-result-error {
    margin-top: 4px;
    font-size: 13px;
    line-height: 1.5;
  }
  .mas-result {
    color: #1b6b32;
  }
  .mas-result-error {
    color: #d92d20;
  }
  .mas-factor {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
  }
  .mas-value {
    width: 200px;
    color: #17191c;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    &.is-empty {
      color: #6e747c;
    }
  }
  .mas-hint {
    color: #6e747c;
    font-size: 13px;
    line-height: 1.5;
  }
  .mas-note {
    margin-top: 10px;
    padding-left: 120px;
  }
  .mas-error {
    margin-top: 10px;
    padding-left: 120px;
    color: #d92d20;
    font-size: 13px;
  }
  // 폰(제조사 마이페이지에서 여는 창): 이름표를 줄이고 값 아래 줄에 버튼
  @media (max-width: 480px) {
    .mas-label {
      flex-basis: 64px;
    }
    .mas-factor {
      flex-wrap: wrap;
      row-gap: 4px;
    }
    .mas-value {
      width: 100%;
      line-height: 40px;
    }
    .mas-note,
    .mas-error {
      padding-left: 64px;
    }
  }
}
</style>
